import React, { useState } from 'react'
import { useLocale, Card, Button, StockItem, ImageUpload, Spinner, Badge } from '@betina/ui'
import { useQuery, useMutation } from '../hooks/useApi.js'
import { stockApi, aiApi } from '../lib/api.js'

const LOCATIONS = ['fridge', 'freezer', 'cupboard']
const LOCATION_ICONS = { fridge: '🧊', freezer: '❄️', cupboard: '🗄️' }

export function Stock() {
  const { t } = useLocale()
  const [activeLocation, setActiveLocation] = useState('fridge')
  const [showAddForm, setShowAddForm] = useState(false)
  const [showScan, setShowScan] = useState(false)
  const [scanResult, setScanResult] = useState(null)
  const [scanning, setScanning] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [form, setForm] = useState({ name: '', quantity: 1, unit: 'unit', expiry_date: '' })

  const { data: items, loading, refetch } = useQuery(
    () => stockApi.list(),
    []
  )

  const { mutate: createItem } = useMutation((data) => stockApi.create(data))
  const { mutate: deleteItem } = useMutation((id) => stockApi.delete(id))
  const { mutate: updateItem } = useMutation(({ id, ...fields }) => stockApi.update(id, fields))
  const { mutate: bulkCreate } = useMutation((stockItems) => stockApi.bulkCreate(stockItems))

  const filtered = (items ?? []).filter((i) => i.location === activeLocation)

  async function handleAdd() {
    if (!form.name.trim()) return
    await createItem({ ...form, location: activeLocation })
    setForm({ name: '', quantity: 1, unit: 'unit', expiry_date: '' })
    setShowAddForm(false)
    refetch()
  }

  async function handleDelete(item) {
    if (!confirm(`Supprimer ${item.name} ?`)) return
    await deleteItem(item.id)
    refetch()
  }

  async function handleScan(base64, mediaType) {
    if (!base64) { setScanResult(null); return }
    setScanning(true)
    try {
      const res = await aiApi.analyzeFridge({ imageBase64: base64, mediaType, location: activeLocation })
      setScanResult(res.scan)
    } finally {
      setScanning(false)
    }
  }

  async function applyScan() {
    if (!scanResult?.items?.length) return
    await bulkCreate(
      scanResult.items.map((i) => ({
        name: i.nameFr || i.name,
        quantity: i.quantity ?? 1,
        unit: i.unit ?? 'unit',
        location: activeLocation,
        expiryDate: i.estimatedExpiry ?? null,
        addedVia: 'image_scan',
      }))
    )
    setScanResult(null)
    setShowScan(false)
    refetch()
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">{t('stock')}</h1>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setShowScan(true)}>
            📷 {t('scan')}
          </Button>
          <Button size="sm" onClick={() => setShowAddForm(true)}>
            + {t('add')}
          </Button>
        </div>
      </div>

      {/* Location tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {LOCATIONS.map((loc) => (
          <button
            key={loc}
            onClick={() => setActiveLocation(loc)}
            className={`flex-1 py-1.5 text-sm rounded-lg transition-colors flex items-center justify-center gap-1 ${
              activeLocation === loc ? 'bg-white shadow text-gray-800 font-medium' : 'text-gray-500'
            }`}
          >
            {LOCATION_ICONS[loc]} {t(loc)}
          </button>
        ))}
      </div>

      {/* Items list */}
      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <Card className="text-center py-8">
          <p className="text-gray-400 text-sm">Aucun article dans {t(activeLocation)}</p>
          <Button size="sm" className="mt-3" onClick={() => setShowScan(true)}>
            📷 Scanner pour remplir automatiquement
          </Button>
        </Card>
      ) : (
        <Card>
          {filtered.map((item) => (
            <StockItem
              key={item.id}
              item={item}
              onEdit={setEditItem}
              onDelete={handleDelete}
            />
          ))}
        </Card>
      )}

      {/* Add form overlay */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/40 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 space-y-3">
            <p className="font-semibold text-gray-800">Ajouter un article — {t(activeLocation)}</p>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Nom de l'article…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              autoFocus
            />
            <div className="flex gap-2">
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: parseFloat(e.target.value) }))}
                placeholder="Quantité"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                min="0"
                step="0.5"
              />
              <select
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                {['unit', 'g', 'kg', 'ml', 'l', 'tbsp', 'tsp', 'cup'].map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <input
              type="date"
              value={form.expiry_date}
              onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
            <div className="flex gap-2">
              <Button className="flex-1 justify-center" onClick={handleAdd}>{t('save')}</Button>
              <Button variant="secondary" className="flex-1 justify-center" onClick={() => setShowAddForm(false)}>{t('cancel')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Scan overlay */}
      {showScan && (
        <div className="fixed inset-0 bg-black/40 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 space-y-3">
            <p className="font-semibold text-gray-800">📷 {t('scan_fridge')}</p>
            <ImageUpload onUpload={handleScan} label="Prendre une photo du frigo / placard" />

            {scanning && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Analyse en cours…
              </div>
            )}

            {scanResult && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-emerald-700">
                  {scanResult.items?.length ?? 0} articles détectés
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {scanResult.items?.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <span>•</span>
                      <span>{item.nameFr || item.name}</span>
                      <span className="text-gray-400">{item.quantity} {item.unit}</span>
                      {item.estimatedExpiry && (
                        <Badge color="yellow" className="ml-auto">{item.estimatedExpiry}</Badge>
                      )}
                    </div>
                  ))}
                </div>
                {scanResult.mealSuggestions?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Suggestions avec ces ingrédients :</p>
                    <div className="flex flex-wrap gap-1">
                      {scanResult.mealSuggestions.map((m, i) => (
                        <Badge key={i} color="green">{m}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button className="flex-1 justify-center" onClick={applyScan}>
                    Ajouter au stock
                  </Button>
                  <Button variant="secondary" className="flex-1 justify-center" onClick={() => setScanResult(null)}>
                    Refaire
                  </Button>
                </div>
              </div>
            )}

            {!scanning && !scanResult && (
              <Button variant="secondary" className="w-full justify-center" onClick={() => setShowScan(false)}>
                {t('cancel')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
